import { Button, Text } from '@mantine/core';
import { DocumentWorkflowTemplateInfo } from '@dealstack/shared/lib/generated';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	FooterActionsRow,
	StepContainer,
	SummaryCard,
	SummaryDocumentItem,
	SummaryDocumentList,
	SummaryTitleRow,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/SendDocumentsModal.styled';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';

interface Props {
	documents: DocumentWorkflowTemplateInfo[];
	recipientCount: number;
	onBack: () => void;
	onSend: () => void;
	isSending: boolean;
}

/** Confirm step: a last read-only look at what is being sent and to how many recipients. The
 * email note is composed on the picker step, per the design. Sending happens here. */
export const ConfirmSendModal = ({
	documents,
	recipientCount,
	onBack,
	onSend,
	isSending,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();

	const sendLabel = tMipManage(
		documents.length === 1
			? 'send_documents.confirm.send_count_one'
			: 'send_documents.confirm.send_count_other',
		{ count: documents.length }
	);
	const recipientsLabel = tMipManage(
		recipientCount === 1
			? 'send_documents.confirm.recipients_one'
			: 'send_documents.confirm.recipients_other',
		{ count: recipientCount }
	);

	return (
		<StepContainer>
			<SummaryCard>
				<SummaryTitleRow>
					<Icon variant="document" size="lg" />
					<Icon variant="user" size="lg" />
					<Text fw={600}>{sendLabel}</Text>
				</SummaryTitleRow>
				<SummaryDocumentList>
					{documents.map((document) => (
						<SummaryDocumentItem key={document.id}>
							<Icon variant="document" size="lg" />
							<Text size="xs" title={document.name}>
								{document.name}
							</Text>
						</SummaryDocumentItem>
					))}
				</SummaryDocumentList>
				<Text size="xs" c="dark.0">
					{recipientsLabel}
				</Text>
			</SummaryCard>
			<FooterActionsRow>
				<Button variant="outline" onClick={onBack} disabled={isSending}>
					{tCommonUi('common_buttons.back')}
				</Button>
				<Button
					data-testid="send-documents-confirm-send"
					onClick={onSend}
					loading={isSending}
				>
					{sendLabel}
				</Button>
			</FooterActionsRow>
		</StepContainer>
	);
};
